import React, { createContext, useContext, useEffect, useMemo, useState } from "react"

const STORAGE_KEY = "analytics.range"

const DEFAULT_STATE = {
  preset: "today",
  customStart: "",
  customEnd: ""
}

export const AnalyticsRangeContext = createContext({
  ...DEFAULT_STATE,
  range: null,
  setPreset: () => {},
  setCustomRange: () => {},
  resetRange: () => {}
})

const pad = value => String(value).padStart(2, "0")

const toDateInputValue = date => {
  const year = date.getFullYear()
  const month = pad(date.getMonth() + 1)
  const day = pad(date.getDate())

  return `${year}-${month}-${day}`
}

const parseDateInputValue = value => {
  if (!value) return null

  const [year, month, day] = value.split("-").map(Number)
  if (!year || !month || !day) return null

  return new Date(year, month - 1, day)
}

const startOfDay = date => {
  const next = new Date(date)
  next.setHours(0, 0, 0, 0)
  return next
}

const endOfDay = date => {
  const next = new Date(date)
  next.setHours(23, 59, 59, 999)
  return next
}

const shiftDays = (date, days) => {
  const next = new Date(date)
  next.setDate(next.getDate() - days)
  return next
}

const resolvePresetDates = (preset, customStart, customEnd) => {
  const now = new Date()
  const todayStart = startOfDay(now)
  const todayEnd = now

  switch (preset) {
    case "yesterday": {
      const day = shiftDays(todayStart, 1)
      return {
        startDate: startOfDay(day),
        endDate: endOfDay(day)
      }
    }
    case "last_7_days":
      return {
        startDate: startOfDay(shiftDays(todayStart, 6)),
        endDate: todayEnd
      }
    case "last_30_days":
      return {
        startDate: startOfDay(shiftDays(todayStart, 29)),
        endDate: todayEnd
      }
    case "last_90_days":
      return {
        startDate: startOfDay(shiftDays(todayStart, 89)),
        endDate: todayEnd
      }
    case "custom": {
      const start = parseDateInputValue(customStart) || todayStart
      const end = parseDateInputValue(customEnd) || now
      const safeStart = start <= end ? start : end
      const safeEnd = start <= end ? end : start

      return {
        startDate: startOfDay(safeStart),
        endDate: endOfDay(safeEnd)
      }
    }
    case "today":
    default:
      return {
        startDate: todayStart,
        endDate: todayEnd
      }
  }
}

const getInitialState = () => {
  if (typeof window === "undefined") {
    return DEFAULT_STATE
  }

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (!stored) return DEFAULT_STATE

    const parsed = JSON.parse(stored)
    if (!parsed || typeof parsed !== "object") return DEFAULT_STATE

    return {
      preset: parsed.preset || DEFAULT_STATE.preset,
      customStart: parsed.customStart || "",
      customEnd: parsed.customEnd || ""
    }
  } catch {
    return DEFAULT_STATE
  }
}

export const AnalyticsRangeProvider = ({ children }) => {
  const [state, setState] = useState(DEFAULT_STATE)
  const [hydrated, setHydrated] = useState(false)

  useEffect(() => {
    setState(getInitialState())
    setHydrated(true)
  }, [])

  useEffect(() => {
    if (!hydrated || typeof window === "undefined") return

    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  }, [state, hydrated])

  const range = useMemo(() => {
    const { startDate, endDate } = resolvePresetDates(
      state.preset,
      state.customStart,
      state.customEnd
    )

    return {
      preset: state.preset,
      customStart: state.customStart,
      customEnd: state.customEnd,
      startDate,
      endDate,
      startMs: startDate.getTime(),
      endMs: endDate.getTime(),
      startValue: toDateInputValue(startDate),
      endValue: toDateInputValue(endDate),
      hasCustomRange: state.preset === "custom"
    }
  }, [state])

  const value = useMemo(
    () => ({
      ...state,
      range,
      setPreset: preset =>
        setState(current => ({
          ...current,
          preset,
          customStart:
            preset === "custom"
              ? current.customStart || toDateInputValue(new Date())
              : current.customStart,
          customEnd:
            preset === "custom"
              ? current.customEnd || toDateInputValue(new Date())
              : current.customEnd
        })),
      setCustomRange: ({ start, end }) =>
        setState(current => ({
          ...current,
          preset: "custom",
          customStart: start || current.customStart,
          customEnd: end || current.customEnd
        })),
      resetRange: () => setState(DEFAULT_STATE)
    }),
    [state, range]
  )

  return (
    <AnalyticsRangeContext.Provider value={value}>
      {children}
    </AnalyticsRangeContext.Provider>
  )
}

export const useAnalyticsRange = () => useContext(AnalyticsRangeContext)
